import React, { useEffect, useRef, useState } from 'react';
import { sopFire } from '../../../utils/sopSwal';
import StageChrome from '../components/StageChrome';
import SortableBoard from '../components/SortableBoard';
import useGameTimer from '../components/useGameTimer';
import { ASSET, reportOnSubmit, stagecheck, submitStage } from '../../../utils/sopGame';
import '../stages.css';

/** Initial scrambled order: { id, img, pos } where pos = correct index */
const INITIAL = [
  { id: 'box1', img: 's2-1.png', pos: 4 },
  { id: 'box2', img: 's2-2.png', pos: 3 },
  { id: 'box3', img: 's2-3.png', pos: 2 },
  { id: 'box4', img: 's2-4.png', pos: 1 },
  { id: 'box5', img: 's2-5.png', pos: 0 },
];

export default function Stage2({ user, onComplete, reportId, initialSeconds }) {
  const [items, setItems] = useState(INITIAL);
  const [locked, setLocked] = useState({});
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const [score, setScore] = useState(0);
  const baseScoreRef = useRef(0);
  const [sending, setSending] = useState(false);
  const timeeRef = useRef('00:00:00');
  const { display } = useGameTimer(initialSeconds, (hms) => {
    timeeRef.current = hms;
  });

  useEffect(() => {
    stagecheck(user)
      .then((p) => {
        setScore(p);
        baseScoreRef.current = p;
      })
      .catch(console.error);
  }, [user]);

  const recountLocks = (list, prevLocked) => {
    const nextLocked = { ...prevLocked };
    let delta = 0;
    list.forEach((item, index) => {
      const correct = item.pos === index;
      const was = !!nextLocked[item.id];
      if (correct && !was) {
        nextLocked[item.id] = true;
        delta += 1;
      } else if (!correct && was) {
        nextLocked[item.id] = false;
        delta -= 1;
      }
    });
    return { nextLocked, delta };
  };

  const moveItem = (fromId, toIndex) => {
    setItems((prev) => {
      const lockedNow = lockedRef.current;
      const fromIndex = prev.findIndex((x) => x.id === fromId);
      if (fromIndex < 0 || fromIndex === toIndex || lockedNow[fromId]) return prev;
      const target = prev[toIndex];
      if (target && lockedNow[target.id]) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      const { nextLocked } = recountLocks(next, lockedNow);
      lockedRef.current = nextLocked;
      setLocked(nextLocked);
      const lockedCount = Object.values(nextLocked).filter(Boolean).length;
      setScore(baseScoreRef.current + lockedCount);
      return next;
    });
  };

  const correctCount = items.filter((it, i) => it.pos === i).length;

  const submit = async () => {
    if (sending) return;
    let points1 = 0;
    items.forEach((it, i) => {
      if (it.pos === i) points1 += 1;
    });
    setSending(true);
    try {
      await submitStage(user, {
        points: score,
        points1,
        round: 2,
        timee: timeeRef.current,
        reportId,
      });
      await reportOnSubmit(user, { points: score, timee: timeeRef.current, round: 2, reportId });
      const msg =
        correctCount === 5
          ? "You've mapped the journey right! Let's proceed to unravel more facts"
          : "Unfortunately, not all points of the journey were mapped correctly. Let's move forward to the next stage and unravel more facts.";
      await sopFire({ text: msg });
      onComplete();
    } catch (err) {
      console.error(err);
      sopFire({ text: err.message || 'Submit failed' });
      setSending(false);
    }
  };

  const reset = () => {
    setItems(INITIAL);
    setLocked({});
    lockedRef.current = {};
    setScore(baseScoreRef.current);
  };

  const boardItems = items.map((it) => ({
    id: it.id,
    src: ASSET.img(it.img),
  }));

  return (
    <StageChrome
      className="sop-stage-sort"
      title="An event that significantly contributed to our freedom was the Salt March. Arrange the salt crystals in the correct chronological order of the milestones in the Salt March"
      score={score}
      timerDisplay={display}
    >
      <SortableBoard items={boardItems} locked={locked} cols={5} onReorder={moveItem} />
      <div className="sop-actions">
        <button type="button" className="sop-img-btn" disabled={sending} onClick={submit}>
          <img src={ASSET.img('submit.png')} alt="Submit" />
        </button>
        <button type="button" className="sop-img-btn" onClick={reset}>
          <img src={ASSET.img('reset.png')} alt="Reset" />
        </button>
      </div>
    </StageChrome>
  );
}
